import React from 'react';
import { Head } from '@inertiajs/react';
import { AEO_DATA } from '~/marketing/marketingData';
import { legal } from '~/marketing/legalData';
import { faq } from '~/marketing/faqData';

/* The head tags every public page needs: a description, a canonical URL, and
   the Open Graph and Twitter pairs that decide what a shared link looks like.

   **One component rather than the tags repeated nine times.** They have to agree
   — og:title that disagrees with <title>, or a canonical that disagrees with the
   path, is worse than neither, because it is wrong rather than absent. The nine
   pages pass a title, a description and a path and get a consistent set.

   **Why the origin is a constant and not the request host.** A canonical URL
   names the one address a page should be indexed under, so it is a published
   fact about the site rather than a property of the request that happens to be
   in flight — the same reason the prices live in `marketingData.js` rather than
   being computed. It also has to survive being rendered at build time, where
   there is no request to read a host from.

   **`head-key` on every tag.** Inertia's head manager pairs the tags already in
   the document against the ones a new page wants, keyed on this attribute.
   Without a key each tag gets `data-inertia=""` and the pairing falls back to
   document order, which still lands on the right answer but churns the DOM on
   every visit. Naming them is free and says what each one is.

   **`og:image` is a 1200x630 card**, built 2026-10-05 by `bin/brand-rasters`
   and committed as a build artifact like the favicons. It is assembled from the
   logo project's own `share-mark.svg` — which `logo-rules.md` specifies as "Mark
   in 1200x630 card coordinates, placed at x=64 y=52", the card's geometry
   without the card — plus the home page's h1. Replace the export and re-run the
   script; never hand-edit the PNG.

   **It is one card for every page, not one per page.** A per-page image would
   mean rendering eleven of them and keeping each in step with a heading, for a
   gain nobody has measured. The card says what the product is, which is the
   thing a shared link needs to convey whichever page it points at.

   `summary_large_image` rather than `summary` because the card is 1.91:1; the
   square-thumbnail card type would letterbox it. */

const ORIGIN = `https://${AEO_DATA.brand.domain}`;

/* The share card. Absolute, because a consumer fetching it has no page to
   resolve a relative path against — this is the one URL here that is read by
   something other than a browser that already has the document.
   `crawler_directives_test` asserts the file exists and is the stated size, so a
   renamed artifact fails rather than shipping a broken preview nobody sees. */
const CARD = {
  url: `${ORIGIN}/brand/og-card.png`,
  width: 1200,
  height: 630,
  alt: 'Spruce — when an AI names one company in your city, make it yours.',
};

/* JSON-LD, as one `@graph` rather than a script per entity.

   **Who the publisher is, said once.** Organization and WebSite are on every
   page and carry `@id`s the page-specific entities point at, so a consumer
   reading any single page can resolve "who makes this" without crawling to a
   different one — which is the question an assistant is answering when it
   decides whether to name a product.

   **The address is a string, not a PostalAddress.** schema.org accepts both, and
   the structured form would mean splitting `legal.entityAddress` into five
   fields that can disagree with the one the legal pages print. One copy of an
   address beats a tidier shape, which is the reason legalData.js exists at all.

   `logo` points at the 180px app icon, which clears the 112px minimum consumers
   apply. There is no dedicated brand image to use yet — see the og:image note
   below. */
const organization = {
  '@type': 'Organization',
  '@id': `${ORIGIN}/#organization`,
  name: legal.product,
  // The business, as the alternate name. No `legalName`: that field is for a
  // registered company name, and the company's legal name appears nowhere on
  // the site by decision — see legalData.js.
  alternateName: legal.business,
  url: `${ORIGIN}/`,
  logo: `${ORIGIN}/icon-180.png`,
  email: legal.supportEmail,
  address: legal.entityAddress,
};

const website = {
  '@type': 'WebSite',
  '@id': `${ORIGIN}/#website`,
  url: `${ORIGIN}/`,
  name: legal.product,
  publisher: { '@id': `${ORIGIN}/#organization` },
};

/* The product itself, with the three tiers as machine-readable offers.

   **Only on the pages that are about the product** — the home page and pricing.
   Putting it on the terms page would claim that page is the software, and a
   consumer that believes the markup would describe the wrong thing.

   Prices come from `AEO_DATA.plans`, the one place the site states them, and
   `structured_data_test` reads them back against `Billing::Plans` — an offer is
   a price claim a machine will quote, so it has to agree with what the checkout
   actually charges. */
export const softwareApplication = {
  '@type': 'SoftwareApplication',
  '@id': `${ORIGIN}/#software`,
  name: legal.product,
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  url: `${ORIGIN}/`,
  publisher: { '@id': `${ORIGIN}/#organization` },
  description:
    'Tracks how AI assistants answer when customers ask who to call in a city, which businesses get named, and which sources the answers are built from.',
  offers: AEO_DATA.plans.map((plan) => ({
    '@type': 'Offer',
    name: plan.name,
    price: plan.monthly.toFixed(2),
    priceCurrency: 'USD',
    category: 'subscription',
    url: `${ORIGIN}/pricing`,
    description: `${plan.count} ${plan.unit}, ${plan.allowance} queries a month.`,
  })),
};

/* A blog post, as a BlogPosting published by the organisation.

   **The headline is the post's title from `blog/posts.json`, the same value
   its h1 renders**, so the markup cannot describe a different page from the
   one a reader sees. No `author` person: the work is the company's, and naming
   somebody would be the one place on the site a person is credited. */
export const article = ({ headline, path, published }) => ({
  '@type': 'BlogPosting',
  '@id': `${ORIGIN}${path}#article`,
  headline,
  url: `${ORIGIN}${path}`,
  datePublished: published,
  author: { '@id': `${ORIGIN}/#organization` },
  publisher: { '@id': `${ORIGIN}/#organization` },
  isPartOf: { '@id': `${ORIGIN}/#website` },
});

/* The blog's index, listing every post with the same fields its own page
   carries — so the listing and each post agree about what it is called and
   when it was published. */
export const blog = (posts) => ({
  '@type': 'Blog',
  '@id': `${ORIGIN}/blog#blog`,
  url: `${ORIGIN}/blog`,
  name: `${legal.product} blog`,
  publisher: { '@id': `${ORIGIN}/#organization` },
  isPartOf: { '@id': `${ORIGIN}/#website` },
  blogPost: posts.map((p) => ({
    '@type': 'BlogPosting',
    '@id': `${ORIGIN}${p.path}#article`,
    headline: p.title,
    url: `${ORIGIN}${p.path}`,
    datePublished: p.published,
  })),
});

/* The FAQ, as structured question-and-answer.

   **Serialised from the same array the page renders**, which is the only thing
   that makes this honest: the markup has to describe content visible on the
   page, and generating both from `faqData.js` means there is no second copy that
   can describe questions nobody can read. `structured_data_test` asserts every
   marked-up answer appears in the response body.

   Paragraphs are joined with a blank line rather than a space, because an answer
   with two paragraphs read as one run-on sentence is a worse answer to quote
   than the one on the page. */
export const faqPage = {
  '@type': 'FAQPage',
  '@id': `${ORIGIN}/faq#faq`,
  publisher: { '@id': `${ORIGIN}/#organization` },
  mainEntity: faq.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a.join('\n\n') },
  })),
};

export function Seo({ title, description, path, schema = [] }) {
  const url = `${ORIGIN}${path}`;
  const graph = {
    '@context': 'https://schema.org',
    '@graph': [ organization, website, ...schema ],
  };

  return (
    <Head title={title}>
      <meta head-key="description" name="description" content={description} />
      <link head-key="canonical" rel="canonical" href={url} />

      <meta head-key="og:type" property="og:type" content="website" />
      <meta head-key="og:site_name" property="og:site_name" content={AEO_DATA.brand.name} />
      <meta head-key="og:title" property="og:title" content={title} />
      <meta head-key="og:description" property="og:description" content={description} />
      <meta head-key="og:url" property="og:url" content={url} />
      <meta head-key="og:image" property="og:image" content={CARD.url} />
      <meta head-key="og:image:width" property="og:image:width" content={CARD.width} />
      <meta head-key="og:image:height" property="og:image:height" content={CARD.height} />
      <meta head-key="og:image:alt" property="og:image:alt" content={CARD.alt} />

      <meta head-key="twitter:card" name="twitter:card" content="summary_large_image" />
      <meta head-key="twitter:title" name="twitter:title" content={title} />
      <meta head-key="twitter:description" name="twitter:description" content={description} />
      <meta head-key="twitter:image" name="twitter:image" content={CARD.url} />
      <meta head-key="twitter:image:alt" name="twitter:image:alt" content={CARD.alt} />

      {/* dangerouslySetInnerHTML because Inertia's head renderer appends it raw;
          a `children` string would be HTML-escaped and the JSON would arrive as
          entities. JSON.stringify cannot emit `</script`, so there is nothing to
          break out of the tag. */}
      <script
        head-key="ld-json"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(graph) }}
      />
    </Head>
  );
}
